import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { useGoogleMapsLoader } from "hooks/googleMapsLoader";
import { mnt } from "theme/maintenanceTokens";

/**
 * N1 Release 1 - CLIENT PORTAL locations map.
 *
 * A deliberately lean sibling of the internal `PlanLocationsMap`. It
 * reuses the same Google Maps loader + imperative marker/InfoWindow
 * pattern, but plots PLAIN markers with NO status: a client sees "here
 * are your sites," nothing about our internal work-order pipeline. There
 * is no status field on the point type at all, so nothing internal can
 * leak through this surface by construction.
 *
 * Release 2 may add a client-safe status layer (e.g. has-activity vs not,
 * derived only from records the client can already see). That is a
 * deliberate later decision; Release 1 is markers only.
 */

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // center of US

export interface ClientMapPoint {
  locationId: string | null;
  profileId: string;
  name?: string | null;
  address?: any;
  latitude: number | null;
  longitude: number | null;
}

interface ClientLocationsMapProps {
  points: ClientMapPoint[];
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  onOpenLocation?: (locationId: string | null) => void;
  height?: number | string;
}

/** A teardrop pin SVG path (Google Maps Symbol). */
const PIN_PATH =
  "M 0,0 C -2.5,-6 -9,-8 -9,-15 A 9,9 0 1,1 9,-15 C 9,-8 2.5,-6 0,0 z";

export default function ClientLocationsMap(props: ClientLocationsMapProps) {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  if (loadError) {
    return (
      <div
        style={{
          height: 440,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: mnt.bgSubtle,
          borderRadius: 8,
          color: mnt.textMuted,
          fontFamily: "Poppins, sans-serif",
          fontSize: 13,
        }}
      >
        Map could not be loaded.
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div
        style={{
          height: 440,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: mnt.bgSubtle,
          borderRadius: 8,
          color: mnt.textMuted,
          fontFamily: "Poppins, sans-serif",
          fontSize: 13,
        }}
      >
        Loading map...
      </div>
    );
  }
  return <TheMap {...props} />;
}

function TheMap({
  points,
  selectedId,
  onSelect,
  onOpenLocation,
  height = 440,
}: ClientLocationsMapProps) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const boundsFittedRef = useRef(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [activeInfoId, setActiveInfoId] = useState<string | null>(null);

  // Only points with real coordinates can be plotted; the rest are
  // surfaced as a small "N not on the map" note so the client still knows
  // the count is complete.
  const mappable = useMemo(
    () =>
      (points || []).filter(
        (p) =>
          p.latitude != null &&
          p.longitude != null &&
          !Number.isNaN(Number(p.latitude)) &&
          !Number.isNaN(Number(p.longitude)),
      ),
    [points],
  );
  const notMappable = (points?.length ?? 0) - mappable.length;

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    infoWindowRef.current = new google.maps.InfoWindow();
    setMapLoaded(true);
  }, []);

  const openInfoWindow = useCallback(
    (p: ClientMapPoint, position: google.maps.LatLng) => {
      const iw = infoWindowRef.current;
      const map = mapRef.current;
      if (!iw || !map) return;

      const addr =
        p.address && typeof p.address === "object"
          ? [p.address.line1, p.address.city, p.address.state]
              .filter(Boolean)
              .join(", ")
          : "";

      const container = document.createElement("div");
      container.style.cssText =
        "font-family:Poppins,sans-serif;font-size:12px;padding:2px 4px 0;min-width:180px;";

      const nameEl = document.createElement("div");
      nameEl.style.cssText = "font-weight:600;color:#000;font-size:13px;";
      nameEl.textContent = p.name || "Location";
      container.appendChild(nameEl);

      if (addr) {
        const addrEl = document.createElement("div");
        addrEl.style.cssText =
          "color:#5f5e5a;font-size:11px;margin:2px 0 2px;";
        addrEl.textContent = addr;
        container.appendChild(addrEl);
      }

      // "Open location" - navigates to the location detail page (the same
      // page the team uses; the client is scoped to their own org's
      // locations server-side). Only when a locationId + handler exist.
      if (typeof onOpenLocation === "function" && p.locationId) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.textContent = "Open location";
        btn.style.cssText =
          "margin-top:6px;padding:5px 12px;border-radius:4px;border:none;" +
          `background:${mnt.link};color:#fff;font-family:Poppins,sans-serif;` +
          "font-size:12px;font-weight:600;cursor:pointer;";
        btn.addEventListener("click", () => {
          onOpenLocation(p.locationId);
        });
        container.appendChild(btn);
      }

      iw.setContent(container);
      iw.setPosition(position);
      iw.open({ map });
      setActiveInfoId(p.locationId ?? p.profileId);
    },
    [onOpenLocation],
  );

  // Reset the fitted flag when the mappable set size changes.
  useEffect(() => {
    boundsFittedRef.current = false;
  }, [mappable.length]);

  // (Re)render markers whenever the input changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    markersRef.current.forEach((mk) => mk.setMap(null));
    markersRef.current.clear();

    const bounds = new google.maps.LatLngBounds();
    let renderedAny = false;

    mappable.forEach((p) => {
      const id = (p.locationId ?? p.profileId) as string;
      const isSelected = selectedId === id;

      const position = new google.maps.LatLng(
        Number(p.latitude),
        Number(p.longitude),
      );

      const marker = new google.maps.Marker({
        position,
        map,
        title: p.name || undefined,
        zIndex: isSelected ? 3 : 1,
        icon: {
          path: PIN_PATH,
          // Uniform pins - no status axis. Selected pin is larger with a
          // heavier stroke so a click reads back on the map.
          fillColor: mnt.link,
          fillOpacity: 1,
          strokeColor: mnt.link,
          strokeWeight: isSelected ? 2.5 : 1,
          scale: isSelected ? 1.5 : 1.2,
          anchor: new google.maps.Point(0, 0),
        } as google.maps.Symbol,
      });

      marker.addListener("click", () => {
        if (typeof onSelect === "function") onSelect(id);
        openInfoWindow(p, position);
      });

      markersRef.current.set(id, marker);
      bounds.extend(position);
      renderedAny = true;
    });

    if (renderedAny && !boundsFittedRef.current) {
      map.fitBounds(bounds, 48);
      boundsFittedRef.current = true;
    }

    if (activeInfoId && !markersRef.current.has(activeInfoId)) {
      infoWindowRef.current?.close();
      setActiveInfoId(null);
    }
    // eslint-disable-next-line
  }, [mappable, mapLoaded, selectedId]);

  const fitAll = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const bounds = new google.maps.LatLngBounds();
    let any = false;
    markersRef.current.forEach((mk) => {
      const pos = mk.getPosition();
      if (pos) {
        bounds.extend(pos);
        any = true;
      }
    });
    if (any) map.fitBounds(bounds, 48);
    if (typeof onSelect === "function") onSelect(null);
  }, [onSelect]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <GoogleMap
        mapContainerStyle={{
          width: "100%",
          height: typeof height === "number" ? `${height}px` : height,
          borderRadius: "8px",
        }}
        zoom={4}
        center={DEFAULT_CENTER}
        onLoad={onLoad}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          // WHEEL SCROLLS THE PAGE, NOT THE MAP.
          //
          // The default ("auto") gives the wheel to the map whenever the
          // cursor is over it. This map is 440px tall near the top of a
          // scrolling page, so on a client with only a handful of sites
          // it covers most of the viewport and the page simply refuses
          // to scroll — it is silently zooming instead. A client with
          // many sites never notices, because they scroll with the
          // cursor over the long list below.
          //
          // "cooperative" is Google's answer for a map embedded in a
          // scrolling page: the wheel scrolls the page, ctrl/⌘ + wheel
          // zooms, and dragging still pans. Two-finger pan on touch.
          gestureHandling: "cooperative",
        }}
      />

      {/* Fit all */}
      <button
        type="button"
        onClick={fitAll}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          background: "#fff",
          border: `1px solid ${mnt.divider}`,
          borderRadius: 4,
          padding: "6px 12px",
          fontSize: 12,
          fontFamily: "Poppins, sans-serif",
          fontWeight: 500,
          cursor: "pointer",
          boxShadow: "0 2px 6px rgba(0,0,0,.2)",
        }}
      >
        Fit all
      </button>

      {/* Not-mappable note - no status legend, since all pins mean the
          same thing (one of the client's sites). */}
      {notMappable > 0 && (
        <div
          style={{
            position: "absolute",
            left: 10,
            bottom: 10,
            background: "#fff",
            border: `1px solid ${mnt.divider}`,
            borderRadius: 6,
            padding: "6px 10px",
            fontFamily: "Poppins, sans-serif",
            fontSize: 11,
            color: mnt.textFaint,
            boxShadow: "0 2px 6px rgba(0,0,0,.15)",
          }}
        >
          {notMappable} not on the map
        </div>
      )}
    </div>
  );
}
